import { defineNuxtPlugin } from '#app'
import { _isEqualWith } from '#imports'

export default defineNuxtPlugin(() => {
  const utils = {
    roundNumber: (value: number, decimals = 2) => {
      if (isNaN(value)) {
        return 0
      }
      const factor = Math.pow(10, decimals)
      return Math.round(value * factor) / factor
    },
    roundNumberToTensInferior: (value: number) => {
      if (isNaN(value)) {
        return 0
      }
      if (value < 10) {
        return value
      }
      return Math.floor(value / 10) * 10
    },
    haversineDistanceBetweenPoints: (lat1: number, lon1: number, lat2: number, lon2: number) => {
      const R = 6371e3
      const p1 = (lat1 * Math.PI) / 180
      const p2 = (lat2 * Math.PI) / 180
      const deltaLon = lon2 - lon1
      const deltaLambda = (deltaLon * Math.PI) / 180
      const d =
        Math.acos(
          Math.sin(p1) * Math.sin(p2) + Math.cos(p1) * Math.cos(p2) * Math.cos(deltaLambda),
        ) * R
      // console.log('haversine distance', d)
      return d
    },
    pythagoreanDistanceBetweenPoints: (lat1: number, lon1: number, lat2: number, lon2: number) => {
      const R = 6371e3
      const x = (lon2 - lon1) * Math.cos((lat1 + lat2) / 2)
      const y = lat2 - lat1
      const d = Math.sqrt(x * x + y * y) * R
      // console.log('pythagorean distance', d)
      return d
    },
    isFirstLetterVowel: (str: string) => {
      const firstLetter = str.charAt(0).toLowerCase()
      const vowels = [
        'a',
        'e',
        'i',
        'o',
        'u',
        'á',
        'é',
        'í',
        'ó',
        'ú',
        'à',
        'è',
        'ì',
        'ò',
        'ù',
        'â',
        'ê',
        'î',
        'ô',
        'û',
        'ä',
        'ë',
        'ï',
        'ö',
        'ü',
      ]
      return vowels.includes(firstLetter)
    },
    isElementInViewport: (el: HTMLElement) => {
      const rect = el.getBoundingClientRect()
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      )
    },
    setFocusPosition: (el: HTMLElement) => {
      // For accessibility purpose.
      // Usage exemples: after an autoscroll, after a DOM mutation, etc.
      const previousTabIndex = el.getAttribute('tabindex')
      el.setAttribute('tabindex', '-1')
      el.focus({
        preventScroll: true,
      })
      el.blur()
      if (previousTabIndex !== null) {
        el.setAttribute('tabindex', previousTabIndex)
      } else {
        el.removeAttribute('tabindex')
      }
    },
    getCommitmentLabel(duration: string, timePeriod: string | null) {
      if (!timePeriod || timePeriod === 'year') {
        switch (duration) {
          case '1_hour':
          case '2_hours':
          case 'half_day':
            return 'few_hours'
          default:
            return 'few_days'
        }
      }

      switch (timePeriod) {
        case 'day':
          if (duration === '1_hour' || duration === '2_hours') {
            return 'few_hours_a_week'
          }
          return 'few_days_a_week'
        case 'week':
          if (duration === '1_hour' || duration === '2_hours' || duration === 'half_day') {
            return 'few_hours_a_week'
          }
          return 'few_days_a_week'
        case 'month':
          if (duration === '1_hour' || duration === '2_hours' || duration === 'half_day') {
            return 'few_hours_a_month'
          }
          return 'few_days_a_month'
      }

      return null
    },
    // todo: refactor with useForm
    isFormDirty: (form: Record<string, unknown>, originalForm: Record<string, unknown>) => {
      return !_isEqualWith(form, originalForm, unorderedArrayEqual)
    },
  }

  return {
    provide: {
      utils,
    },
  }
})

function unorderedArrayEqual(objValue: Record<string, unknown>, othValue: Record<string, unknown>) {
  if (Array.isArray(objValue) && Array.isArray(othValue)) {
    if (objValue.length !== othValue.length) {
      return false
    }
    // Compare arrays as sets (order-insensitive)
    return objValue.every((item1) =>
      othValue.some((item2) => _isEqualWith(item1, item2, unorderedArrayEqual)),
    )
  }
  // Let lodash handle other types
  return undefined
}
